<template>
	<div>
		<v-data-table
			:headers="headers_local"
			:items="items_local"
			:page.sync="page"
			:items-per-page="countPerpage"
			:hide-default-header="!$vuetify.breakpoint.smAndDown"
			hide-default-footer
			disable-sort
			:item-class="rowClass"
			@click:row="onRowClick($event)"
		>
			<template v-slot:top>
				<div class="table-head" v-if="items.length !== 0">Showing&nbsp;&nbsp;<span>{{ (page-1) * countPerpage + 1}}-{{ page * countPerpage > items.length ? items.length : page * countPerpage }}</span>&nbsp;&nbsp;of&nbsp;&nbsp;<span>{{ items.length }}</span>&nbsp;&nbsp;{{ titleText }}</div>
				<div class="table-head" v-else>Showing&nbsp;&nbsp;<span>{{ page * countPerpage > items.length ? items.length : page * countPerpage }}</span>&nbsp;&nbsp;of&nbsp;&nbsp;<span>{{ items.length }}</span>&nbsp;&nbsp;{{ titleText }}</div>
			</template>
			<template v-if="!$vuetify.breakpoint.smAndDown" v-slot:header="{ props: { headers } }">
				<thead>
					<v-draggable v-model="headers_local" tag="tr">
						<th 
							v-for="item in headers" 
							:key="item.value"
							class="header-th"
							@mouseover="isHover = item.value"
							@mouseout="isHover = ''">
							<div
								v-if="item.showable"
								class="header-item"
								:style="(item.value === 'currency' || item.value === 'gross' || item.value === 'tax' || item.value === 'value') ? 'justify-content: flex-end':''">
								<div v-if="item.type === 'option'">
									<v-checkbox
										color="info"
										:input-value="allSelect"
										:disabled="currentPageItems.length === 0"
										:indeterminate="indeterminate"
										@change="onSelectAllChange($event)"
									></v-checkbox>
								</div>
								<div class="header-item-sub">
									<div class="header-item-text">{{ item.text }}</div>
									<SortUpDown 
										v-if="item.type === 'sort'" 
										class="icon icon-sort-up-down" 
										:selected="isHover === item.value" 
										:selected_color="color" 
										@click="onSort(item.value)"/>
								</div>
								<v-menu offset-y nudge-left="200" >
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<ShowSearch 
												class="icon" 
												v-if="item.searchable" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div class="search-box">
										<v-text-field
											placeholder="Search Title..."
											outlined
											dense
											hide-details="auto"
											color="#00AAFD" 
											:value="current_key === item.value ? searchValue : ''"
											@input="searchValue = $event"
											@click.stop
										/>
										<div class="search-box-button">
											<div @click.stop="onClean">CLEAR</div>
											<div @click="onSearch(item.value)">SEARCH</div>
										</div>
									</div>
								</v-menu>
								<v-menu offset-y nudge-left="200" min-width="100px" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<FirstList 
												v-if="item.value === 'currency'" 
												class="icon icon-sort-up-down" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div v-for="currency in currencies" :key="currency.id">
										<v-checkbox
											class="filter-box mt-0"
											v-model="select_currency"
											:label="`${currency.display_name}(${currency.symbol})`"
											:value="currency.id"
											color="info"
											dense
											hide-details>
										</v-checkbox>
									</div>
								</v-menu>
							</div>
						</th>
					</v-draggable> 
				</thead>
			</template>
			<template v-slot:[`item.select`]="{ item }">		
				<v-checkbox
					v-model="item.select"
					@click.native.stop
					color="info"
				></v-checkbox>	
			</template>
			<template v-slot:[`item.number`]="{ item }">			
				<span :class="item.status === approved_status ? 'text-disable':'text-able'" >{{ item.number }}</span>
			</template>
			<template v-slot:[`item.status`]="{ item }">			
				<div :class="item.status === approved_status ? 'text-disable' : item.status === pending_status ? 'text-able-red':'text-able'" >
					{{ item.status }}
					<v-tooltip v-if="item.status === pending_status" top>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								small
								class="mr-2"
								color="#F45252"
								v-bind="attrs"
								v-on="on"
							>
								mdi-information-outline
							</v-icon>
						</template>
						<span>This claim has not been approved yet.</span>
					</v-tooltip>
				</div>
			</template>
			<template v-slot:[`item.submitted_date`]="{ item }">			
				<span :class="item.status === approved_status ? 'text-disable':'text-able'" >{{ formatDate(item.submitted_date) }}</span>
			</template>
			<template v-slot:[`item.date`]="{ item }">			
				<span :class="item.status === approved_status ? 'text-disable':'text-able'" >{{ formatDate(item.date) }}</span>
			</template>
			<template v-slot:[`item.supplier`]="{ item }">
				<v-user-avatar
					size="24"
					class="text-caption text-decoration-none"
					:code="item.supplier.code"
					:image="item.supplier.image"
				/>			
				<span class="ml-2" :class="item.status === approved_status ? 'text-disable':'text-able'"  v-html="item.supplier.display_name"></span>
			</template>
			<template v-slot:[`item.currency`]="{ item }">			
				<div :class="item.status === approved_status ? 'text-disable':'text-able'" style="text-align: center;">{{ item.currency.symbol }}</div>
			</template>
			<template v-slot:[`item.gross`]="{ item }">			
				<div :class="item.status === approved_status ? 'text-disable':'text-able'" style="text-align: right; margin-right: 15px">{{ formatNumber(item.gross) }}</div>
			</template>
			<template v-slot:[`item.tax`]="{ item }">			
				<div :class="item.status === approved_status ? 'text-disable':'text-able'" style="text-align: right; margin-right: 15px">{{ formatNumber(item.tax) }}</div>
			</template>
			<template v-slot:[`item.value`]="{ item }">			
				<div :class="item.status === approved_status ? 'text-disable':'text-able'" style="text-align: right; margin-right: 15px">{{ formatNumber(item.value) }}</div>
			</template>
			<template v-slot:[`item.desc`]="{ item }">			
				<span :class="item.status === approved_status ? 'text-disable':'text-able'" v-html="item.desc"></span>
			</template>
			<template v-slot:[`item.remark`]="{ item }">			
				<span :class="item.status === approved_status ? 'text-disable':'text-able'"  v-html="item.remark"></span>
			</template>
		</v-data-table>
		<div class="pagination d-print-none">
			<v-pagination
				v-model="page"
				:color="color"
				:length="getPaginationTotal()"
				:total-visible="$vuetify.breakpoint.xs ? 3: 5"
				circle
			></v-pagination>
			<div class="pagination-text">show</div>
			<div class="pagination-text-page">{{ countPerpage}}/page</div>
			<v-menu offset-y>
				<template v-slot:activator="{ on, attrs }">
					<div v-bind="attrs" v-on="on"><Down class="down"/></div>
				</template>
				<v-list>
					<v-list-item-title v-for="(pageOption, index) in pageOptions" :key="index" class="option-text" @click="onClickOption(pageOption)">
						{{pageOption}}
					</v-list-item-title>
				</v-list>
			</v-menu>
		</div>
	</div>
</template>
<script>
import SortUpDown from '@/components/icons/sort_up_down';
import ShowSearch from '@/components/icons/show_search';
import FirstList from "@/components/icons/first_list";
import Down from '@/components/icons/down';

import config from "@/config.js";
import moment from 'moment';

const FONT_COLOR = config.font_color;
const APPROVED_STATUS = 'Approved';
const PENDING_STATUS = 'Pending';

export default {
	name: 'ApprovalTable',
	props: {
		color: {
			type: String,
			default: ""
		},
		currencies: {
			type: Array,
			default: () => [],
		},
		statuses: {
			type: Array,
			default: () => [],
		},
		header: {
			type: Array,
			default: () => []
		},
		items: {
			type: Array,
			default: () => []
		},
		titleText: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			headers_local: this.header,
			items_local: [],
			items_local_original: [],
			page: 1,
			countPerpage: 10,
			pageOptions: config.pageOptions,
			searchValue: '',
			sortByAscent: false,
			isHover: '',
			current_key: '',
			select_currency: this.currencies.map(currency => { return currency.id }),
			approved_status: APPROVED_STATUS,
			pending_status: PENDING_STATUS,
		}
	},
	components: {
		SortUpDown, 
		ShowSearch,
		FirstList,
		Down
	},
	created() {
		this.items_local = this.items;
		this.items_local_original = this.items;
	},
	watch: {
		header() {
			this.headers_local = this.header;
		},
		items() {
			this.items_local = this.items;
			this.items_local_original = this.items;
		},
		select_currency() {
			this.items_local = this.items_local_original.filter(item => {
				return this.select_currency.includes(item.currency.id);
			});
		},
		items_local: {
			handler(newVal) { 
				this.$emit('button-visible', newVal);
			},
			deep: true
		}
	},
	computed: {
		currentPageItems() {
			const start = (this.page - 1) * this.countPerpage;
			return this.items_local.slice(start, start + this.countPerpage);
		},
		indeterminate() {
			return this.currentPageItems.some(item => item.select === true) && !this.allSelect;
		},
		allSelect() {
			return this.currentPageItems.length !== 0 &&
				this.currentPageItems.every(item => item.select === true);
		},
	},
	methods: {
		rowClass(item) {
			if (!item.select) {
				return 'd-print-none';
			}
		},
		onRowClick(item) {
			this.$emit('row-item', item);
		},
		onSelectAllChange(value) {
			this.currentPageItems.forEach(item => {
				this.$set(item, 'select', Boolean(value));
			});
		},
		getPaginationTotal() {
			return Math.ceil(this.items_local.length / this.countPerpage);
		},
		onClickOption(option) {
			this.countPerpage = option;
		},
		onSort(key) {
			this.sortByAscent = !this.sortByAscent;

			const approved_arr = this.items_local.filter(d => {return d.status === this.approved_status});
			const other_arr = this.items_local.filter(d => {return d.status !== this.approved_status});

			if (key === 'value') {
				this.sortByAscent
					? this._sort_ascent_number(approved_arr, key)
					: this._sort_descent_number(approved_arr, key);

				this.sortByAscent
					? this._sort_ascent_number(other_arr, key)
					: this._sort_descent_number(other_arr, key);

				const sorted_arr = [];
				sorted_arr.push(...other_arr);
				sorted_arr.push(...approved_arr);

				this.items_local = sorted_arr;
			} else {
				this.sortByAscent
					? this._sort_ascent(approved_arr, key)
					: this._sort_descent(approved_arr, key);
					
				this.sortByAscent
					? this._sort_ascent(other_arr, key)
					: this._sort_descent(other_arr, key);

				const sorted_arr = [];
				sorted_arr.push(...other_arr);
				sorted_arr.push(...approved_arr);

				this.items_local = sorted_arr;
			}	
		},
		onSearch(key) {
			if (this.searchValue.length === 0) return;

			this.current_key = key;
				
			const arr_filter = this._filterKey(this.items_local_original, key, this.searchValue);
			const arr_replace = JSON.parse(JSON.stringify(arr_filter));

			// to hightlight the kewwords
			const regex = new RegExp(this.searchValue, 'gi');
			arr_replace.forEach(arr => {
				if (arr[key]) {
					if ( typeof(arr[key]) === 'object' ) {
						arr[key].display_name = arr[key].display_name.replace(regex, (keyword) => {
							return `<font color="${FONT_COLOR}">${keyword}</font>`;
						});
					} else {
						arr[key] = arr[key].replace(regex, (keyword) => {
							return `<font color="${FONT_COLOR}">${keyword}</font>`;
						});
					}
				}
			});
			this.items_local = arr_replace;
		},
		onClean() {
			this.searchValue = '';
			this.items_local = this.items_local_original;
		},
		formatDate(date) {
			if(!date) return null;
			return moment(date).format('DD MMM YYYY');
		},
		formatNumber(num) {
			let res = 0;
			if (Number.isInteger(num)) {
				const integer = num.toLocaleString();
				res = integer + '.00';
			} else {
				const num_arr = (num.toString()).split('.');
				const integer = Number(num_arr[0]).toLocaleString();

				if (num_arr.length > 1) {
					if (num_arr[1].length === 1) {
						res = integer + '.' + num_arr[1] + '0';
					} else {
						res = integer + '.' + num_arr[1];
					}
					
				} else {
					res = integer + '.00' ;
				}
			}
			return res;
		},
		_sort_ascent(arr, key) {
			if (key === 'code') {
				key = 'job';
			}

			if (key === 'display_name') {
				key = 'job';
			}

			arr.sort((a, b) => {
				let valueA = '';
				let valueB = '';
				if (a[key]) {
					valueA = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
				}

				if (b[key]) {
					valueB = typeof(b[key]) === 'object' ? b[key].display_name.toUpperCase() : b[key].toUpperCase();
				}

				if (valueA < valueB) {
					return -1;
				}
				if (valueA > valueB) {
					return 1;
				}

				return 0;
			});
		},
		_sort_descent(arr, key) {
			if (key === 'code') {
				key = 'job';
			}

			if (key === 'display_name') {
				key = 'job';
			}

			arr.sort((a, b) => {
				let valueA = '';
				let valueB = '';

				if (a[key]) {
					valueA = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
				} 

				if (b[key]) {
					valueB = typeof(b[key]) === 'object' ? b[key].display_name.toUpperCase() : b[key].toUpperCase();
				}

				if (valueA > valueB) {
					return -1;
				}
				if (valueA < valueB) {
					return 1;
				}

				return 0;
			});
		},
		_sort_ascent_number(arr, key) {
			arr.sort((a, b) => {
				return a[key] - b[key];
			});
		},
		_sort_descent_number(arr, key) {
			arr.sort((a, b) => {
				return b[key] - a[key];
			});
		},
		_filterKey(arr, key, filter) {
			let fliter_Arr = [];
			const filter_UpperCase = filter.toUpperCase();
			fliter_Arr = arr.filter(a => {
				let A = '';
				if (a[key]) {
					A = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
				} 
				return A.includes(filter_UpperCase);
			});
			
			return fliter_Arr;
		},
	}
}
</script>
<style lang="scss" scoped>
.table-head {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	height: 48px;
	padding: 0 20px;
	background: #FAFAFA;
	color: #192A3E;
	font-family: Roboto;
	font-style: normal;
	font-weight: normal;
	font-size: 16px;
	line-height: 24px;
	border-bottom: 1px solid #C9C9C9;
	border-radius: 4px 4px 0 0;
	span {
		font-weight: bold;
	}
}
.header-th {
	background: #FAFAFA;
	&:hover {
		background: #EAEAEA;
		cursor: move;
	}
}
.header-item {
	display: flex;
  flex-flow: row nowrap;
	justify-content: space-between;
  align-items: center;
}
.header-item-sub {
	display: flex;
  flex-flow: row nowrap;
	justify-content: flex-start;
  align-items: center;
}
.header-th:hover .icon {
	visibility: visible; 
}
.header-item-text {
	white-space: nowrap;
	text-overflow: ellipsis;
}
.icon-wrap {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
}
.icon {
	visibility: hidden;
	width: 14px;
  height: 14px;
	&:hover {
		cursor: pointer;
	}
}
.icon-sort-up-down {
  margin-left: 6px;
}
.search-box {
	padding: 13px;
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
	background: #FFFFFF;
	border-radius: 4px;
	.search-box-button {
		margin-top: 9px;
		display: flex;
		flex-flow: row nowrap;
		justify-content: space-between;
		align-items: center;
		div {
			display: flex;
			flex-flow: row nowrap;
			justify-content: center;
			align-items: center;
			border-radius: 4px;
			padding: 0 21px;

			font-family: Roboto;
			font-style: normal;
			font-weight: 500;
			font-size: 14px;
			height: 36px;

			&:hover {
				cursor: pointer;
			}
		}
		:first-child {
			border: 1px solid rgba(25, 42, 62, 0.7);
			margin-right: 10px;
			color: rgba(25, 42, 62, 0.7);
		}
		:last-child {
			border: 1px solid #00AAFD;
			background: #00AAFD;
			color: #FFFFFF;
		}
	}
}
.filter-box {
	background: #FFFFFF;
}
.text-able {
	color: rgba(0, 0, 0, 0.87);
}
.text-able-red {
	color: #EE0202;
}
.text-disable {
	color: #C4C4C4;
}
.pagination {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	.pagination-text {
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 16px;
		color: rgba(25, 42, 62, 0.5);
	}
	.pagination-text-page {
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 16px;
		color: #192A3E;
		margin: 0 8px;
	}
	.down {
		width: 12px;
		height: 12px;
		margin-top: 10px;
		&:hover {
			cursor: pointer;
		}
	}
	
}
::v-deep {
	.option-text {
		margin: 0 4px;
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		color: #192A3E;
		&:hover {
			cursor: pointer;
		}
	}
	.v-data-table {
		border-radius: 4px;
		border: 1px solid #C9C9C9;
	}
}
</style>